<template>
  <va-form
    :id="isEdit ? footer.id : ''"
    ref="form"
    :item="footer"
    v-model="form"
    :modify-before-send="prepareFormBeforeSave"
    :before-save="beforeSave"
    :after-save="handleAfterSave"
    :required-fields-set="requiredFieldsSet">
    <Modal
      width="448"
      :value="displayVersionHistoryModal"
      :title="$t('resources.footers.history_modal_title')"
      :cta-text="$t('resources.pages.page_history_modal_cta_text')"
      :cta-disabled="!selectedVersion"
      :cta-loading="changeVersionLoading"
      @confirm="confirmVersionChange"
      @cancel="cancelVersionChange"
      actions-border>
      <v-select
        class="spacing-top--20"
        v-model="selectedVersion"
        :items="versionsOptions"
        :label="$t('resources.footers.version_select_label')"
        :placeholder="$t('resources.footers.version_select_placeholder')"
        persistent-placeholder
        :menu-props="{ offsetY: true, offsetOverflow: true }"
        hide-details="auto">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
        <template v-slot:item="{ item }">
          <div class="v-list-item__content">
            <div class="v-list-item__title">{{ item.text }}</div>
          </div>
          <div v-if="item.disabled" class="ml-auto">
            <v-chip class="in-use-chip" :color="'subtitle'" outlined :text-color="'subtitle'">
              {{ $t("resources.pages.version_in_use") }}
            </v-chip>
          </div>
        </template>
      </v-select>
    </Modal>
    <v-card class="spacing-bottom--24">
      <div class="d-flex">
        <div class="d-flex flex-column">
          <v-card-title>Details</v-card-title>
          <v-card-subtitle class="spacing-bottom--16">
            {{ $t("resources.footers.footers_details_block_subtitle") }}
          </v-card-subtitle>
        </div>
        <v-card-actions class="ml-auto">
          <v-checkbox
            color="primary"
            v-model="form.isDefault"
            :label="$t('resources.footers.set_as_default')"></v-checkbox>
        </v-card-actions>
      </div>
      <div v-if="form.updatedAt" class="spacing-bottom--16">
        <div class="updated-at-label">
          {{ $t("resources.footers.fields.last_updated") }}
        </div>
        <div class="d-flex">
          <div class="updated-at-time">
            {{ getFullDateAndTime(form.updatedAt) }}
            {{ form.updatedBy && `by ${form.updatedBy.firstName} ${form.updatedBy.lastName}` }}
          </div>
          <div
            v-if="isEdit && versionsOptions.length"
            class="view-history ml-auto"
            @click="displayVersionHistoryModal = true">
            {{ $t("resources.pages.view_history") }}
          </div>
        </div>
      </div>
      <va-text-input
        required
        v-model="form.name"
        source="name"
        class="spacing-bottom--16"
        :label="$t('resources.footers.fields.name')"
        :placeholder="$t('resources.footers.footers_name_placeholder')"></va-text-input>

      <va-autocomplete-input
        v-model="form.domain"
        source="domain"
        :disabled="isDomainProvided"
        class="spacing-bottom--16"
        required
        :placeholder="$t('resources.footers.footers_domain_placeholder')"
        :label="$t('resources.footers.domain')"
        :choices="domainsList"
        :min-chars="1"
        cache-items
        @change="domainChanged"></va-autocomplete-input>

      <div class="page-wrapper" v-if="form.domain">
        <div class="page-label-row">
          <div class="page-label">
            {{ $t("resources.footers.fields.page") }}
          </div>
          <v-btn
            text
            color="primary"
            class="chip-action-button"
            @click="showAddPageDialog = true"
            :ripple="false">
            <v-icon left small>mdi-plus</v-icon>
            {{ $t("resources.footers.add_page") }}
          </v-btn>
        </div>
        <div class="chip-container" v-if="form.pages && form.pages.length > 0">
          <v-chip
            color="primary-20"
            text-color="secondary-btn"
            close
            close-icon="mdi-close"
            @click:close="removePage(page)"
            v-for="page in getPageArray"
            :key="page['@id']">
            <v-icon small left @click.stop="openPageEdit(page)">mdi-open-in-new</v-icon>
            {{ getPageName(page) }}
          </v-chip>
          <div v-if="form.pages.length > 12">
            <v-btn
              v-if="!showAllPageChips"
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = true"
              :ripple="false">
              {{ $t("resources.calendars.show_all") }}
            </v-btn>
            <v-btn
              v-else
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = false"
              :ripple="false">
              {{ $t("resources.calendars.collapse") }}
            </v-btn>
          </div>
        </div>
        <PagePickerModal
          v-model="showAddPageDialog"
          :pages="form.pages || []"
          :domain="form.domain"
          :title="$t('resources.footers.add_page_title')"
          :placeholder="$t('resources.footers.add_page_placeholder')"
          :note="$t('resources.footers.add_page_reassign_warning')"
          in-use-field="currentFooter"
          :current-resource-iri="form['@id']"
          :in-use-message="$t('resources.footers.add_page_in_use_message')"
          @confirm="syncPages" />
      </div>

      <v-select
        v-if="ability.can('view', 'footer desktop layouts')"
        v-model="form.template"
        :label="$t('resources.footers.desktop_layout')"
        :items="footerTemplates"
        :menu-props="{
          offsetOverflow: true,
          offsetY: true,
        }">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
      </v-select>

      <v-select
        v-if="form.template !== 'audio_forms'"
        v-model="form.logoPosition"
        :label="$t('resources.footers.mobile_layout')"
        :items="FOOTER_LOGO_POSITIONS"
        :menu-props="{
          offsetOverflow: true,
          offsetY: true,
        }">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
      </v-select>

      <VaImageDragAndDropInput
        class="spacing-bottom--16"
        :item-id="form.logo"
        :label="$t('resources.footers.default_logo')"
        @update:photo="updateFooterLogo"></VaImageDragAndDropInput>

      <va-tinymce-input
        :key="versionKey"
        source="customContent"
        :title="$t('resources.footers.content')"
        :current-content="form.customContent"
        :editor-height="320"
        :stripped-down="true"
        @update="updateContent" />
    </v-card>

    <v-card class="spacing-bottom--16">
      <v-card-title>{{ $t("resources.footers.footer_items_block_title") }}</v-card-title>
      <v-card-subtitle class="spacing-bottom--11">
        {{ $t("resources.footers.footer_items_block_subtitle") }}
      </v-card-subtitle>
      <va-array-input
        :key="versionKey"
        v-model="form.menu.menuItems"
        v-slot="props"
        source="menu.menuItems"
        :label="false"
        :hide-details="'auto'"
        :add-button-text="$t('generic.add_menu_link')">
        <v-select
          v-model="form.menu.menuItems[props.index].type"
          source="type"
          persistent-placeholder
          :placeholder="$t('resources.footers.footers_menu_item_type_placeholder')"
          :hide-details="'auto'"
          :label="$t('resources.footers.item_type')"
          :items="MENU_ITEM_TYPES"
          :menu-props="{
            offsetOverflow: true,
            offsetY: true,
          }"
          @change="changeMenuType(props.index)">
          <template v-slot:append>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
        </v-select>
        <va-text-input
          v-model="form.menu.menuItems[props.index].text"
          class="spacing-horizontal--16"
          :label="$t('resources.footers.item_title')"
          :placeholder="$t('resources.footers.footers_menu_item_title_placeholder')"
          required
          v-bind="props"
          source="text"></va-text-input>
        <va-autocomplete-input
          v-if="form.menu.menuItems[props.index].type === 3"
          v-model="form.menu.menuItems[props.index].category"
          :label="$t('resources.footers.page_category')"
          source="category"
          :placeholder="$t('resources.footers.footers_menu_page_category_placeholder')"
          required
          :filter="{ ...resourcesFilter }"
          v-bind="props"
          reference="page_categories"
          :error-messages="getFieldError('category', form.menu.menuItems[props.index])"
          @change="form.menu.menuItems[props.index].categoryError = null"
          item-value="@id"
          :min-chars="0"
          :items-per-page="5"
          showLoadMore>
        </va-autocomplete-input>
        <va-text-input
          v-if="form.menu.menuItems[props.index].type === 2"
          v-model="form.menu.menuItems[props.index].link"
          :label="$t('resources.footers.external_link')"
          :placeholder="$t('resources.footers.footers_menu_item_link_placeholder')"
          required
          v-bind="props"
          :rules="[v => !!urlValidation(v) || $t('generic.validation.url')]"
          source="link"></va-text-input>
        <va-autocomplete-input
          v-if="form.menu.menuItems[props.index].type !== 3 && form.menu.menuItems[props.index].type !== 2"
          v-model="form.menu.menuItems[props.index].page"
          v-bind="props"
          :label="$t('resources.footers.existing_page')"
          :placeholder="$t('resources.footers.footers_menu_page_placeholder')"
          :filter="{ ...resourcesFilter, status: [1, 2] }"
          source="page"
          reference="pages"
          item-value="@id"
          :error-messages="getFieldError('page', form.menu.menuItems[props.index])"
          @change="form.menu.menuItems[props.index].pageError = null"
          required
          searchQuery="headline"
          :min-chars="0"
          :items-per-page="5"
          showLoadMore>
        </va-autocomplete-input>
        <v-select
          class="spacing-left--16"
          v-model="form.menu.menuItems[props.index].action"
          persistent-placeholder
          :menu-props="{ offsetY: true, offsetOverflow: true }"
          :hide-details="'auto'"
          :label="$t('generic.open_in')"
          :items="MENU_TAB_TYPES">
          <template v-slot:append>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
        </v-select>
        <va-number-input
          style="display: none"
          disabled
          :value="(form.menu.menuItems[props.index].position = props.index + 1)"
          required
          v-bind="props"
          source="position"></va-number-input>
      </va-array-input>

      <v-divider v-if="form.menu.menuItems && form.menu.menuItems.length > 0" class="my-6"></v-divider>

      <v-select
        v-if="form.menu.menuItems && form.menu.menuItems.length > 0"
        class="spacing-top--16"
        v-model="form.fontSize"
        persistent-placeholder
        :label="$t('resources.footers.menu_item_font_size')"
        :items="FOOTER_MENU_ITEM_FONT_SIZES"
        :menu-props="{
          offsetOverflow: true,
          offsetY: true,
        }">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
      </v-select>

      <v-select
        v-if="form.menu.menuItems && form.menu.menuItems.length > 0"
        class="spacing-top--16"
        v-model="form.menuPosition"
        persistent-placeholder
        :label="$t('resources.footers.menu_position')"
        :placeholder="$t('resources.footers.footers_menu_position_placeholder')"
        :items="FOOTER_MENU_POSITIONS"
        :menu-props="{
          offsetOverflow: true,
          offsetY: true,
        }">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
      </v-select>

      <div class="d-flex align-center spacing-top--16">
        <v-switch
          v-model="form.categoriesAlwaysDisplayed"
          :label="$t('resources.footers.categories_always_displayed')"
          color="primary"
          :disabled="form.template === 'default' || form.template === 'audio_forms'"
          hide-details="auto">
        </v-switch>
        <v-tooltip v-if="form.template === 'default' || form.template === 'audio_forms'" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small class="ml-2" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
          </template>
          <span>{{ $t('resources.footers.categories_always_displayed_unsupported') }}</span>
        </v-tooltip>
      </div>
    </v-card>

    <v-card>
      <v-card-title>{{ $t("resources.footers.colors_title") }} </v-card-title>
      <v-card-subtitle>{{ $t("resources.footers.colors_subtitle") }}</v-card-subtitle>

      <div class="d-flex">
        <v-card-text>
          <va-color-picker
            @is-hovered="e => (isBackgroundFieldHovered = e)"
            class="mb-4"
            :title="$t('resources.footers.background_color')"
            v-model="form.backgroundColor">
          </va-color-picker>
          <va-color-picker
            v-if="form.template !== 'default'"
            @is-hovered="e => (isBorderFieldHovered = e)"
            class="mb-4"
            :title="$t('resources.footers.border_color')"
            allow-empty
            v-model="form.borderColor">
          </va-color-picker>
          <va-color-picker
            @is-hovered="e => (isMenuItemTextFieldHovered = e)"
            class="mb-4"
            :title="$t('resources.footers.menu_item_color')"
            v-model="form.menuItemColor">
          </va-color-picker>
          <va-color-picker
            v-if="form.template !== 'default' && form.template !== 'audio_forms'"
            @is-hovered="e => (isHeadingFieldHovered = e)"
            class="mb-4"
            :title="$t('resources.footers.heading_color')"
            allow-empty
            v-model="form.headingColor">
          </va-color-picker>
          <v-switch
            v-model="form.showLogoBlock"
            :label="$t('resources.footers.show_logo_block')"
            color="primary"
            hide-details="auto">
          </v-switch>
        </v-card-text>

        <v-card-text class="preview-box">
          <label class="preview-box-label"> {{ $t("generic.preview_box.label") }} </label>
          <div class="preview-box-container">
            <div class="preview-box__template" v-if="form.template === 'default'">
              <div class="label">{{ $t("generic.preview_box.desktop_layout") }}</div>

              <div
                class="preview-box__template-wrapper preview-box__template-wrapper--footer"
                :class="{ 'bg-mask': isBackgroundFieldHovered }"
                :style="{ 'background-color': form.backgroundColor }">
                <div class="preview-box__template-menu preview-box__template-menu--footer">
                  <div
                    class="preview-box__template-menu__item preview-box__template-menu__item--footer"
                    :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                    :style="{ 'background-color': form.menuItemColor }"></div>
                  <div
                    class="preview-box__template-menu__item preview-box__template-menu__item--footer"
                    :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                    :style="{ 'background-color': form.menuItemColor }"></div>
                  <div
                    class="preview-box__template-menu__item preview-box__template-menu__item--footer"
                    :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                    :style="{ 'background-color': form.menuItemColor }"></div>
                </div>

                <div class="preview-box__template-fake-text">
                  <div class="fake-text first"></div>
                  <div class="fake-text second"></div>
                  <div class="fake-text third"></div>
                </div>
              </div>
            </div>

            <div class="preview-box__template" v-else>
              <div class="label">{{ $t("generic.preview_box.desktop_layout") }}</div>
              <div
                class="preview-box__template-wrapper preview-box__template-wrapper--footer-blogs"
                :class="{ 'bg-mask': isBackgroundFieldHovered, 'border-top-mask': isBorderFieldHovered }"
                :style="{ 'background-color': form.backgroundColor, 'border-top-color': form.borderColor }">
                <div class="preview-box__template-menu preview-box__template-menu--footer">
                  <div class="preview-box__template-menu__column">
                    <div
                      v-if="form.template !== 'audio_forms'"
                      class="preview-box__template-menu__item preview-box__template-menu__item--heading"
                      :class="{ 'menu-item-mask': isHeadingFieldHovered }"
                      :style="{ 'background-color': form.headingColor || form.menuItemColor }"></div>
                    <div
                      class="preview-box__template-menu__item preview-box__template-menu__item--footer-blogs"
                      :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                      :style="{ 'background-color': form.menuItemColor }"></div>
                    <div
                      class="preview-box__template-menu__item preview-box__template-menu__item--footer-blogs w-36px"
                      :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                      :style="{ 'background-color': form.menuItemColor }"></div>
                    <div
                      class="preview-box__template-menu__item preview-box__template-menu__item--footer-blogs"
                      :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                      :style="{ 'background-color': form.menuItemColor }"></div>
                  </div>
                  <div class="preview-box__template-menu__column">
                    <div
                      v-if="form.template !== 'audio_forms'"
                      class="preview-box__template-menu__item preview-box__template-menu__item--heading"
                      :class="{ 'menu-item-mask': isHeadingFieldHovered }"
                      :style="{ 'background-color': form.headingColor || form.menuItemColor }"></div>
                    <div
                      class="preview-box__template-menu__item preview-box__template-menu__item--footer-blogs"
                      :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                      :style="{ 'background-color': form.menuItemColor }"></div>
                    <div
                      class="preview-box__template-menu__item preview-box__template-menu__item--footer-blogs"
                      :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                      :style="{ 'background-color': form.menuItemColor }"></div>
                    <div
                      class="preview-box__template-menu__item preview-box__template-menu__item--footer-blogs w-36px"
                      :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                      :style="{ 'background-color': form.menuItemColor }"></div>
                  </div>
                  <div class="preview-box__template-menu__column">
                    <div
                      v-if="form.template !== 'audio_forms'"
                      class="preview-box__template-menu__item preview-box__template-menu__item--heading"
                      :class="{ 'menu-item-mask': isHeadingFieldHovered }"
                      :style="{ 'background-color': form.headingColor || form.menuItemColor }"></div>
                    <div
                      class="preview-box__template-menu__item preview-box__template-menu__item--footer-blogs"
                      :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                      :style="{ 'background-color': form.menuItemColor }"></div>
                    <div
                      class="preview-box__template-menu__item preview-box__template-menu__item--footer-blogs w-37px"
                      :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                      :style="{ 'background-color': form.menuItemColor }"></div>
                    <div
                      class="preview-box__template-menu__item preview-box__template-menu__item--footer-blogs w-41px"
                      :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                      :style="{ 'background-color': form.menuItemColor }"></div>
                  </div>
                </div>

                <div
                  v-if="form.showLogoBlock"
                  class="preview-box__template-logo preview-box__template-logo--footer-blogs">
                  {{ $t("generic.preview_box.logo") }}
                </div>
              </div>
            </div>
          </div>

          <div class="preview-box-description" v-html="$t('generic.preview_box.description')"></div>
        </v-card-text>
      </div>
    </v-card>

    <required-field-text></required-field-text>
  </va-form>
</template>

<script>
  import definedAbility from "@/ability";
  // Components
  import VaImageDragAndDropInput from "../custom/VaImageDragAndDropInput";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import RequiredFieldText from "@/components/custom/RequiredFieldText";
  import PagePickerModal from "@/components/custom/PagePickerModal";
  import Modal from "@/components/custom/Modal";

  // Mixins
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  import crudFormMixin from "@/mixins/crudFormMixin";
  import changeMenuType from "@/mixins/changeMenuTypeMethod";
  import providedDomain from "@/mixins/providedDomain";
  import pagesAttachment from "@/mixins/pagesAttachment";

  // Constants
  import {
    MENU_ITEM_TYPES,
    MENU_TAB_TYPES,
    FOOTER_LOGO_POSITIONS,
    FOOTER_MENU_POSITIONS,
    FOOTER_MENU_ITEM_FONT_SIZES,
    FOOTER_TEMPLATES,
  } from "../../constant/index";

  // Utility
  import { convertAssetsPathToAbsoluteLinks, convertAssetsPathToRelativeLinks } from "@/utility/convertAssetsPaths";
  import { parseToHtml } from "@/utility/parseToHtml";
  import { formatMenuItems, formatRestoredMenuItems } from "@/utility/formatMenuItems";
  import { urlValidation } from "@/utility/urlValidation";
  import { removeAlpha } from "@/utility/removeAlpha";
  import { formErrorMessage } from "@/utility/formErrorMessage";
  import { serializeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
  import { cleanupTinymceContent } from "@/utility/cleanupTinymceContent";
  import { getFullDateAndTime } from "@core/utils/filter";

  export default {
    name: "FooterForm",
    components: {
      VaImageDragAndDropInput,
      VaColorPicker,
      RequiredFieldText,
      PagePickerModal,
      Modal,
    },
    mixins: [crudFormMixin, ConnectedDomainsList, changeMenuType, providedDomain, pagesAttachment],
    props: {
      footer: {
        type: Object,
        default: () => {},
      },
      isEdit: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        urlValidation,
        ability: definedAbility(),
        FOOTER_LOGO_POSITIONS,
        FOOTER_TEMPLATES,
        FOOTER_MENU_POSITIONS,
        FOOTER_MENU_ITEM_FONT_SIZES,
        MENU_ITEM_TYPES,
        MENU_TAB_TYPES,
        requiredFormFields: ["name", "domain"],
        form: {},
        isBackgroundFieldHovered: false,
        isMenuItemTextFieldHovered: false,
        isBorderFieldHovered: false,
        isHeadingFieldHovered: false,
        oldDomain: "",
        getFullDateAndTime,
        versions: [],
        versionsOptions: [],
        selectedVersion: null,
        displayVersionHistoryModal: false,
        changeVersionLoading: false,
        versionKey: 0,
      };
    },
    computed: {
      resourcesFilter() {
        return this.form.domain ? { domain: this.form.domain.replace("/api/domains/", "") } : null;
      },
      footerTemplates() {
        return this.$can("create", "audio forms footer")
          ? this.FOOTER_TEMPLATES
          : this.FOOTER_TEMPLATES.filter(x => x.value !== "audio_forms");
      },
    },
    beforeMount() {
      const isDuplicateForm = !this.isEdit && !!this.footer;

      this.form = this.footer ? this.footer : {};
      if (!this.isEdit) {
        delete this.form["@context"];
        delete this.form["@id"];
        delete this.form["@type"];
      }

      if (isDuplicateForm) {
        this.form.pages = [];
        this.form.updatedAt = null;
        this.form.updatedBy = null;
      }

      this.mainContentToShow = this.footer?.customContent || "";
      this.form.backgroundColor = removeAlpha(this.footer?.backgroundColor) || "#FFFFFF";
      this.form.borderColor = this.footer?.borderColor || null;
      this.form.logo = this.footer?.logo?.["@id"] || null;
      this.form.domain = this.footer?.domain["@id"];
      this.form.template = this.footer?.template || "default";
      this.form.logoPosition = this.footer?.logoPosition || FOOTER_LOGO_POSITIONS[0].value;
      this.form.menu = this.footer?.menu || {};
      this.form.menu.domain = this.form.domain;
      this.form.menuItemColor = removeAlpha(this.footer?.menuItemColor) || "#959595";
      this.form.headingColor = removeAlpha(this.footer?.headingColor) || null;
      this.form.menu.menuItems = formatMenuItems(this.footer?.menu.menuItems);
      if (this.isEdit) this.setVersions(this.footer?.versions);
      this.form.menuPosition = this.footer?.menuPosition || 1;
      this.form.fontSize = this.footer?.fontSize || "16px";
      this.form.categoriesAlwaysDisplayed = this.footer?.categoriesAlwaysDisplayed || false;
      this.form.showLogoBlock = this.footer?.showLogoBlock ?? true;
      this.oldDomain = this.footer?.domain;
      this.oldPages = this.footer?.pages || [];
      if (this.footer?.customContent)
        this.updateCustomContent(convertAssetsPathToAbsoluteLinks(this.footer.customContent));

      //Check if it is not edit page and not create new page
      if (!this.isEdit && !!this.footer) {
        delete this.form.menu["@id"];
        delete this.form.menu.id;
        delete this.form.menu["@type"];

        //This conditional have to be here because menu could be without menuItems
        if (this.form.menu.menuItems) {
          this.form.menu.menuItems.map(item => {
            delete item["@id"];
            delete item["@type"];
          });
        }
      }

      if (!this.ability.can("view", "footer desktop layouts")) {
        this.form.template = "default";
      }
      if (!this.ability.can("view", "menu item page category")) {
        this.MENU_ITEM_TYPES = this.MENU_ITEM_TYPES.filter(x => !x.text.includes("Page Category"));
      }
    },
    methods: {
      handleAfterSave(data) {
        this.afterSave();
        if (data?.versions) this.setVersions(data.versions);
      },
      setVersions(versions, versionInUse = versions?.[0]?.version) {
        if (!versions?.length) return;

        this.versions = versions;

        this.versionsOptions = versions.map(versionItem => ({
          text: versionItem.updatedBy
            ? this.$t("resources.pages.saved_by_full", {
                date: getFullDateAndTime(versionItem.date),
                name: versionItem.updatedBy,
              })
            : getFullDateAndTime(versionItem.date),
          value: versionItem.version,
          disabled: versionItem.version === versionInUse,
        }));
      },
      async confirmVersionChange() {
        await this.changeVersion(this.selectedVersion);
        this.cancelVersionChange();
      },
      cancelVersionChange() {
        this.displayVersionHistoryModal = false;
        this.selectedVersion = null;
      },
      async changeVersion(version) {
        try {
          this.changeVersionLoading = true;
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "GET",
            `footers/${this.footer.id}/version/${version}`,
          );

          this.form.name = data.name;
          this.form.template = data.template;
          this.form.logoPosition = data.logoPosition || FOOTER_LOGO_POSITIONS[0].value;
          this.form.menuPosition = data.menuPosition || 1;
          this.form.fontSize = data.fontSize || "16px";
          this.form.categoriesAlwaysDisplayed = data.categoriesAlwaysDisplayed || false;
          this.form.showLogoBlock = data.showLogoBlock ?? true;
          this.form.logo = data.logo?.["@id"] || null;
          this.form.backgroundColor = removeAlpha(data.backgroundColor) || "#FFFFFF";
          this.form.borderColor = data.borderColor ?? null;
          this.form.menuItemColor = removeAlpha(data.menuItemColor) || "#959595";
          this.form.headingColor = removeAlpha(data.headingColor) || null;
          this.updateCustomContent(convertAssetsPathToAbsoluteLinks(data.customContent || ""));
          this.form.menu = {
            ...this.form.menu,
            domain: this.form.domain,
            menuItems: formatRestoredMenuItems(data.menu?.menuItems),
          };
          // Inputs that mount from now on (menu link rows) read their value from va-form's item, so it has to
          // be the restored version too.
          this.$refs.form.formState.item = this.form;
          this.versionKey += 1;
          this.setVersions(this.versions, version);
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.changeVersionLoading = false;
        }
      },
      getFieldError(field, item) {
        if (field === "page") {
          return item.pageError ? [item.pageError] : undefined;
        } else if (field === "category") {
          return item.categoryError ? [item.categoryError] : undefined;
        }
      },
      prepareFormBeforeSave(data) {
        data.pages = data.pages.map(page => page["@id"]);
        data.borderColor = data.borderColor || null;
        data.headingColor = data.headingColor || null;

        if (data.customContent) {
          let htmlElementFromContent;
          if (this.ability.can("upload", "media object to inulti server")) {
            // If for is not empty prepare the shortcode image URLS, to be stripped
            // This functions strips absolute URL's from the image src attribute
            htmlElementFromContent = convertAssetsPathToRelativeLinks(data.customContent);
          } else {
            htmlElementFromContent = parseToHtml(data.customContent);
          }
          cleanupTinymceContent(htmlElementFromContent);

          data.customContent = htmlElementFromContent.body.innerHTML;
        }

        if (data.menu["@id"]) {
          // menu.domain value should be IRI
          data.menu.domain = data.domain;
        }

        /**
         * Serialize shortcodes
         */
        if (data.customContent) {
          const content = parseToHtml(data.customContent);
          serializeDynamicComponents(content);
          data.customContent = content.body.innerHTML;
        } else {
          data.customContent = "";
        }

        return data;
      },
      updateCustomContent(newValue) {
        this.mainContentToShow = newValue;
        let htmlElementFromContent = parseToHtml(this.mainContentToShow);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        cleanupTinymceContent(htmlElementFromContent);
        this.form.customContent = htmlElementFromContent.body.innerHTML;
      },
      domainChanged(domain) {
        if (this.form.menu) {
          this.form.menu.domain = domain;

          if (this.form.menu.menuItems) {
            this.form.menu.menuItems.forEach(item => {
              if (item.page) {
                item.pageError = this.$t("generic.validation.page_must_match_domain");
              }

              if (item.category) {
                item.categoryError = this.$t("generic.validation.page_category_must_match_domain");
              }
              item.page = null;
              item.category = null;
            });
          }
        }

        if (this.oldDomain !== domain) {
          this.form.pages = [];
        } else {
          this.form.pages = this.oldPages;
        }
      },
      updateContent(newValue) {
        this.form.customContent = newValue;
      },
      updateFooterLogo(newValue) {
        if (newValue === "") {
          this.form.logo = null;
        } else {
          this.form.logo = newValue["@id"];
        }
      },
      submit() {
        this.$refs.form.onSubmit();
      },
    },
    watch: {
      "form.menu.menuItems": {
        handler: function (newVal) {
          newVal.map(item => {
            if (!item.action) {
              item.action = MENU_TAB_TYPES[0].value;
            }
          });
        },
      },
      "form.template": {
        handler: function (newVal) {
          if (newVal === "default" || newVal === "audio_forms") {
            this.form.categoriesAlwaysDisplayed = false;
          }
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .page-label {
    @include text-caption-bold();
    color: get-color("subtitle");
  }

  .page-label-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  .page-wrapper {
    margin-bottom: 12px;

    .chip-action-button.v-btn {
      padding: 5px 10px !important;
      height: unset !important;
      min-width: unset;
      color: get-color("secondary-btn") !important;
    }
  }

  .page-chip-group {
    ::v-deep {
      .v-slide-group__content {
        padding: 0;
      }
    }
  }

  .chip-container {
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    row-gap: 6px;
    align-items: center;
  }

  .updated-at-label {
    @include text-caption-bold();
    color: get-color("subtitle");
  }

  .updated-at-time {
    @include text-body-2();
    color: get-color("subtitle");
  }
  .view-history {
    @include text-button-small();
    color: get-color("secondary-btn");
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      color: get-color("same-hover");
    }
  }
  .in-use-chip {
    height: 24px;
    &::v-deep {
      .v-chip__content {
        @include text-caption();
      }
    }
  }
</style>
